/**
 * The `.gitref` marker rule, single-sourced.
 *
 * A git reference is stored as an object at `<name>.gitref`, so three questions
 * follow it everywhere: is this key a reference, what does the user call it, and
 * what is its canonical path. Both the server (listing, read, write) and the
 * client (folder row, breadcrumb) have to answer all three, and they have to
 * answer them IDENTICALLY — a row labelled one way and a breadcrumb labelled
 * another describes two different objects to whoever is reading.
 *
 * They lived on the server alone and the client re-derived the display name with
 * `String.prototype.replace`, which strips the FIRST occurrence anywhere in the
 * basename rather than the suffix: `notes.gitref.v2.gitref` listed as
 * `notes.gitref.v2` and opened as `notes.v2.gitref`. The rule is one rule, so it
 * gets one home rather than a comment asking each side to be careful.
 */

/** The suffix that makes an object a git-reference descriptor. */
export const GIT_REFERENCE_MARKER_SUFFIX = ".gitref";

/** Last path segment of an S3 key or a workspace-relative path. */
const basenameOf = (key: string): string => key.slice(key.lastIndexOf("/") + 1);

/**
 * True if an S3 key (or a workspace-relative path) is a git-reference descriptor.
 *
 * A file named exactly `.gitref` has an empty display name, so it is NOT a valid
 * reference — it stays an ordinary (hidden-looking) file.
 */
export function isGitReferenceKey(key: string | undefined): boolean {
  if (!key) return false;
  const base = basenameOf(key);
  return (
    base.length > GIT_REFERENCE_MARKER_SUFFIX.length && base.endsWith(GIT_REFERENCE_MARKER_SUFFIX)
  );
}

/**
 * Display name for a descriptor: its basename with the marker suffix removed.
 *
 * A SLICE, never a replace — the suffix is a position, not a substring.
 */
export function gitReferenceDisplayName(key: string): string {
  const base = basenameOf(key);
  return base.endsWith(GIT_REFERENCE_MARKER_SUFFIX)
    ? base.slice(0, -GIT_REFERENCE_MARKER_SUFFIX.length)
    : base;
}

/**
 * Append the marker suffix unless the caller already supplied it, so both
 * `docs/api` and `docs/api.gitref` address the same descriptor. Callers pass
 * user input here; idempotence stops `api.gitref.gitref` existing at all.
 */
export function withGitReferenceSuffix(relativePath: string): string {
  return relativePath.endsWith(GIT_REFERENCE_MARKER_SUFFIX)
    ? relativePath
    : `${relativePath}${GIT_REFERENCE_MARKER_SUFFIX}`;
}
